<script setup lang="ts">
/**
 * Хаб проектов — макет 17:765: тулбар с фильтром и поиском, заголовок со
 * сводкой и сетка карточек 354×148 в три колонки.
 *
 * Фильтра «Архив» в макете три, здесь два: архивных проектов в трекере пока
 * нет ни в модели, ни в API (DEV-1500). Вкладка, которая всегда пуста, врёт
 * сильнее, чем её отсутствие.
 *
 * Ленты «Недавняя активность» из макета тоже нет — по той же причине, ручки
 * событий проекта в tracker нет (DEV-1500).
 */
import { AppButton, AppSegmentedControl, AppTextField } from '@vinprop/design-system'
import { AlertTriangle, Folder, Search } from 'lucide-vue-next'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import TrackerEmptyState from '@/components/TrackerEmptyState.vue'
import TrackerToolbar from '@/components/TrackerToolbar.vue'
import CreateProjectModal from '@/features/projects/components/CreateProjectModal.vue'
import ProjectCard from '@/features/projects/components/ProjectCard.vue'
import { useProjectsStore, type ProjectFilter } from '@/features/projects/store'

const { t } = useI18n()

const projects = useProjectsStore()
const route = useRoute()
const router = useRouter()

const filter = ref<ProjectFilter>('all')
const search = ref('')
// Плюс в сайдбаре ведёт сюда с ?create=1: заводить проект зовут с любого
// экрана, а форма живёт на хабе.
const isCreateOpen = ref(route.query.create === '1')
const searchField = ref<HTMLElement | null>(null)

const filters = computed(() => [
  { value: 'all' as const, label: t('projects.filters.all') },
  { value: 'mine' as const, label: t('projects.filters.mine') },
])

// Три числительных, а не одна строка: у русского три формы, и «1 проектов»
// на первом же экране нового пользователя выглядит поломкой.
const summary = computed(() =>
  [
    t('projects.summary.projects', projects.items.length),
    t('projects.summary.issues', projects.openIssueTotal),
    t('projects.summary.mine', projects.myProjectCount),
  ].join(' · '),
)

const isEmpty = computed(
  () => !projects.isLoading && !projects.error && projects.items.length === 0,
)

/** Пусто из-за фильтра — это не «нет проектов», и звать заводить первый незачем. */
const isFiltered = computed(() => filter.value !== 'all' || search.value.trim().length > 0)

const reload = () => projects.load({ filter: filter.value, search: search.value.trim() })

let searchTimer: ReturnType<typeof setTimeout> | undefined

// Запрос на каждую букву не нужен: список короткий, а бэкенд ищет по всем
// проектам, до которых человек допущен.
watch(search, () => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(reload, 300)
})

watch(filter, reload)

const focusSearch = (event: KeyboardEvent) => {
  if (event.key !== 'k' || !(event.metaKey || event.ctrlKey)) return

  event.preventDefault()
  searchField.value?.querySelector('input')?.focus()
}

// Список мог приехать раньше — его грузит лэйаут ради сайдбара; второй раз
// за тем же ходить незачем.
onMounted(() => {
  void projects.ensureLoaded()
  window.addEventListener('keydown', focusSearch)
})

// Флаг в адресе одноразовый: перезагрузка страницы не должна снова открывать
// форму, а «назад» — возвращать в неё.
watch(isCreateOpen, (open) => {
  if (!open && route.query.create) void router.replace({ query: {} })
})

onBeforeUnmount(() => {
  clearTimeout(searchTimer)
  window.removeEventListener('keydown', focusSearch)
})
</script>

<template>
  <TrackerToolbar :breadcrumbs="[{ label: t('nav.projects') }]">
    <AppSegmentedControl
      v-model="filter"
      :options="filters"
      size="small"
      :aria-label="t('projects.filters.label')"
    />

    <div ref="searchField" class="projects__search">
      <AppTextField
        v-model="search"
        size="small"
        filled
        :placeholder="t('projects.searchPlaceholder')"
        :prefix-icon="Search"
        :aria-label="t('projects.searchPlaceholder')"
      />
      <kbd class="projects__kbd">⌘K</kbd>
    </div>

    <AppButton variant="primary" size="small" @click="isCreateOpen = true">
      {{ t('projects.create.action') }}
    </AppButton>
  </TrackerToolbar>

  <div class="projects">
    <h1 class="projects__title">{{ t('nav.projects') }}</h1>
    <p class="projects__summary">{{ summary }}</p>

    <div v-if="projects.isLoading && !projects.items.length" class="projects__grid">
      <div v-for="index in 3" :key="index" class="projects__skeleton" />
    </div>

    <TrackerEmptyState
      v-else-if="projects.error"
      :icon="AlertTriangle"
      :title="t('projects.error.title')"
      :description="t('projects.error.description')"
      tint="var(--color-danger-tint)"
    >
      <AppButton variant="secondary" @click="reload">{{ t('projects.error.retry') }}</AppButton>
    </TrackerEmptyState>

    <TrackerEmptyState
      v-else-if="isEmpty && isFiltered"
      :icon="Search"
      :title="t('projects.nothingFound.title')"
      :description="t('projects.nothingFound.description')"
    />

    <TrackerEmptyState
      v-else-if="isEmpty"
      :icon="Folder"
      :title="t('projects.empty.title')"
      :description="t('projects.empty.description')"
    >
      <AppButton variant="primary" @click="isCreateOpen = true">
        {{ t('projects.create.action') }}
      </AppButton>
    </TrackerEmptyState>

    <div v-else class="projects__grid">
      <ProjectCard
        v-for="project in projects.items"
        :key="project.id"
        :project="project"
        :members="projects.members[project.id]"
      />
    </div>
  </div>

  <CreateProjectModal v-model="isCreateOpen" />
</template>

<style scoped>
.projects {
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 32px 48px;
}

.projects__title {
  margin: 0;
  color: var(--color-text-primary);
  font-size: var(--font-size-h2);
  font-weight: var(--font-weight-semibold);
  line-height: 23px;
}

.projects__summary {
  margin: 20px 0 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-body);
  line-height: 16px;
}

/* Три колонки по 354 при ширине контента 1104 — как в макете; на узком экране
   ряд разбирается сам. */
.projects__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px 21px;
  margin-top: 20px;
}

.projects__skeleton {
  height: 148px;
  border-radius: var(--radius-xl);
  background: var(--color-bg-muted);
}

.projects__search {
  position: relative;
  width: 220px;
}

/* Подсказка сочетания живёт внутри поля — как в макете; поле про неё не знает,
   поэтому кладём поверх и уводим от кликов. */
.projects__kbd {
  position: absolute;
  top: 6px;
  right: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 20px;
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  color: var(--color-text-muted);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-overline);
  pointer-events: none;
}
</style>
